/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { withTranslation } from "react-i18next";

import { Label, UncontrolledTooltip } from "reactstrap";

import Select from "react-select";
import Switch from "react-switch";

// Custom methods
import {
  isFunction,
  isNumber,
  isObject,
} from "../../../Libs/CustomMethods/customMethods";

// Theme style
import "./lightTheme.scss";

const importDarkMode = (mainTheme) => {
  if (mainTheme === "dark") {
    require("./darkTheme.scss");
  }
};

const ChatEvent = ({ t, ...props }) => {
  const { mainTheme } = useSelector((state) => state.Layout);
  const [placeholder, setPlaceholder] = useState(null);
  const [newValue, setNewValue] = useState({});
  const [value, setValue] = useState(null);

  // REFS
  const inputRef = useRef(null);
  const selectRef = useRef(null);
  const selectSkillRef = useRef(null);

  importDarkMode(mainTheme);

  const style = {
    width: props?.width ?? "auto",
    ...props?.style,
  };

  const periodOptions = [
    {
      unity: "minutes",
      label: t("Minutes"),
    },
    {
      unity: "hours",
      label: t("Hours"),
    },
    {
      unity: "days",
      label: t("Days"),
    },
  ];

  const toggleSwitch = () => {
    isFunction(props?.setDisabled) && props?.setDisabled((prev) => !prev);
  };

  const minutesToObj = useCallback((minutes) => {
    if (minutes >= 0) {
      if (minutes % 24 === 0 && (minutes / 24) % 60 === 0) {
        //DIAS EXATOS
        return { value: minutes / 24 / 60, unity: "days" };
      } else {
        if (minutes % 60 === 0) {
          //HORAS EXATAS
          return { value: minutes / 60, unity: "hours" };
        } else {
          //MINUTOS
          return { value: minutes, unity: "minutes" };
        }
      }
    } else {
      return null;
    }
  }, []);

  const objToMinutes = useCallback((obj) => {
    if (obj?.unity) {
      let minutes =
        obj.unity === "days"
          ? obj.value * 24 * 60
          : obj.unity === "hours"
          ? obj.value * 60
          : obj.value;
      return parseInt(minutes);
    }
  }, []);

  // TODO: improve input handlers with debounce
  const handleInput = (e) => {
    let input = inputRef?.current;
    let select = selectRef?.current;
    let selectSkill = selectSkillRef?.current;
    let val = e?.target?.value;
    let regex = new RegExp(/[^0-9]/g);

    if (val && input && regex.test(val)) {
      input.value = val.replace(regex, "");
    } else {
      if (select) {
        let obj = {
          value: val,
          unity: select?.state?.value?.unity,
        };
        let minutes = objToMinutes(obj) || 0;

        isNumber(minutes) &&
          setNewValue((prev) => ({ ...prev, value: minutes }));
        if (selectSkill) {
          setNewValue((prev) => ({
            ...prev,
            skill: selectSkill?.state?.value?.value,
          }));
        }
      }
    }
  };

  const handleSelect = (e) => {
    let input = inputRef?.current;
    let selectSkill = selectSkillRef?.current;
    let unity = e?.unity;

    if (unity && input) {
      let obj = {
        value: input?.value,
        unity: unity,
      };
      let minutes = objToMinutes(obj) || 0;

      isNumber(minutes) && setNewValue((prev) => ({ ...prev, value: minutes }));
    }
    if (selectSkill) {
      setNewValue((prev) => ({
        ...prev,
        skill: selectSkill?.state?.value?.value,
      }));
    }
  };

  const handleSelectSkill = (e) => {
    let input = inputRef?.current;
    let select = selectRef?.current;
    let unity = select?.state?.value?.unity;

    if (unity && input) {
      let obj = {
        value: input?.value,
        unity: unity,
      };
      let minutes = objToMinutes(obj) || 0;

      isNumber(minutes) && setNewValue((prev) => ({ ...prev, value: minutes }));
    }
    setNewValue((prev) => ({ ...prev, skill: e?.value }));
  };

  const handleSubmit = () => {
    isFunction(props?.onChange) && props?.onChange(newValue);
  };

  const tooltips = useCallback(() => {
    return (
      <UncontrolledTooltip placement="bottom" target={`eventInfo-${props?.id}`}>
        {t(props?.tooltip)}
      </UncontrolledTooltip>
    );
  }, [props?.id, props?.tooltip, t]);

  const title = useCallback(() => {
    return (
      <div className="d-flex justify-content-between w-100">
        <Label className="d-flex control-label font-weight-bold align-items-center pr-2">
          {t(props?.title ?? "")}
          <i
            className="fas fa-info-circle px-2 pointer"
            id={`eventInfo-${props?.id}`}
          ></i>
        </Label>
        {props?.disabled !== undefined && (
          <div>
            <div onClick={toggleSwitch}>
              <Switch
                id={`eventToggle-${props?.id}`}
                className="chat-event-switch d-flex align-self-center"
                onColor="#34c38f"
                offColor="#bfc8e2"
                onHandleColor="#222736"
                offHandleColor="#32394E"
                handleDiameter={10}
                width={28}
                height={16}
                checkedIcon={false}
                uncheckedIcon={false}
                checked={!props?.disabled ?? false}
                disabled
                onChange={() => {}}
              />
            </div>
          </div>
        )}
        {tooltips()}
      </div>
    );
  }, [props?.disabled, props?.id, props?.title, t, tooltips]);

  const selectSkill = useCallback(() => {
    return (
      <div className="select2-container selectContainer selectSkill">
        <Select
          ref={selectSkillRef}
          classNamePrefix="select2-selection"
          className={`${props?.disabled ? "isDisabled" : ""}`}
          options={props?.options}
          placeholder={t("Choose") + "..."}
          onChange={handleSelectSkill}
          isDisabled={props?.disabled ?? false}
          isLoading={props?.isLoading}
        />
      </div>
    );
  }, [props?.disabled, t, value, props?.isLoading]);

  const description = useCallback(
    (isSkill) => {
      return (
        <div className="descriptionContainer flex-grow-1">
          <span>
            {isSkill
              ? t(props?.descriptionSkill ?? "")
              : t(props?.description ?? "")}
            :
          </span>
        </div>
      );
    },
    [props?.description]
  );

  const input = useCallback(() => {
    return (
      <div className="select2-container mx-3 inputContainer">
        <input
          size={2}
          type="text"
          ref={inputRef}
          className="form-control"
          placeholder={`Ex: ${placeholder?.value ?? ""}`}
          onChange={handleInput}
          disabled={props?.disabled ?? false}
        />
      </div>
    );
  }, [props?.disabled, value?.value, placeholder]);

  const getItem = (el) => {
    return el !== undefined
      ? periodOptions?.find((e) => e?.unity === el?.unity) ?? null
      : periodOptions[0];
  };

  const getSkillItem = () => {
    return props?.defaultSkill
      ? props?.options?.find((e) => e?.value === props?.defaultSkill) ?? null
      : null;
  };

  const weekDays = useCallback(() => {
    return (
      <div className="select2-container selectContainer">
        <Select
          ref={selectRef}
          classNamePrefix="select2-selection"
          className={`${props?.disabled ? "isDisabled" : ""}`}
          options={periodOptions}
          placeholder={t("Choose") + "..."}
          onChange={handleSelect}
          isDisabled={props?.disabled ?? false}
          isLoading={props?.isLoading}
        />
      </div>
    );
  }, [props?.disabled, t, value, props?.isLoading]);

  // TODO: refactor this method after skill re-render issue is solved
  const updateFields = () => {
    let input = inputRef?.current;
    let select = selectRef?.current?.state;
    let selectSkill = selectSkillRef?.current?.state;
    let obj = props?.defaultValue && minutesToObj(props?.defaultValue);

    if (!props?.isLoading && input && select) {
      if (obj) {
        select.value = getItem(obj) ?? getItem();
        input.value = obj?.value ?? "";
      } else {
        select.value = getItem(placeholder) ?? getItem();
        input.value = "";
      }
      selectSkill && (selectSkill.value = getSkillItem());
    }
  };

  // TODO: temporary code, until fix skill page re-render problem (entire content)
  useEffect(() => {
    let placeholder =
      props?.id === "autoClose"
        ? {
            value: 90,
            unity: "days",
          }
        : {
            value: 5,
            unity: "minutes",
          };
    setPlaceholder(placeholder);
  }, []);

  useEffect(() => {
    isNumber(props?.defaultValue) &&
      setValue(minutesToObj(props?.defaultValue));
  }, [props?.defaultValue]);

  useEffect(() => {
    isObject(props?.placeholder) && setPlaceholder(props?.placeholder);
  }, [props?.placeholder]);

  useEffect(
    () => updateFields(),
    [
      props?.isLoading,
      props?.defaultValue,
      props?.defaultSkill,
      props?.placeholder,
    ]
  );

  useEffect(() => {
    if (props?.options) {
      isNumber(newValue?.value) && newValue?.skill && handleSubmit();
    } else {
      isNumber(newValue?.value) && handleSubmit();
    }
  }, [newValue]);

  return (
    <>
      <div
        className="chatEvent chatEvent-box mb-3 d-flex flex-column rounded p-3"
        style={style}
      >
        <div className="d-flex align-items-center mb-2">{title()}</div>
        <div className="d-flex align-items-center mb-2">
          {props?.options && (
            <>
              {description(true)}
              {selectSkill()}
            </>
          )}
        </div>
        <div className="d-flex align-items-center mt-2">
          {description(false)}
          {input()}
          {weekDays()}
        </div>
      </div>
    </>
  );
};

export default withTranslation()(React.memo(ChatEvent));
